// Canonical alpha basket — one source of truth for the rolling top-10 model
// portfolio with chained-leg accounting. Every alpha-zone surface in the UI
// reads from data/alpha-basket.json (built by scripts/live-tracker/update-alpha-basket.ts).
//
// Design:
// - 20 stable slots indexed 0..19. Symbols rotate through slots.
// - Mid-month: only `lastUpdatedAt` updates; membership is locked.
// - First trading day of a new month: rebalance — close exits at today's close
//   (locks in returnPct), open new legs for the entrants (paired by composite
//   rank to displaced exits).
// - Returns are chained per-slot: sinceInception/MTD/YTD aggregate the legs
//   that fall in the window, weighted by leg.weight across slots.
// - Headline aggregates (today/MTD/YTD/sinceInception) are NEVER stored on
//   disk. They're computed live in /api/alpha-basket and /api/alpha-zone-
//   tracker from scores.json + daily-prices-cache.json on every request.
//   This was the cache that lied about "-2.89% since inception" on 2026-05-06
//   when scores.json had updated but the basket file's pre-computed
//   aggregates hadn't. Killed the denormalization → impossible to lie.

import { compositeScore, isAlphaZoneEligible } from './composite-score';
import type { ScoresFile, DailyCache } from './alpha-zone-tracker';
import { priceOnOrBefore } from './alpha-zone-tracker';

// Reduced 20 → 10 after the cell-grid backtest showed the
// equal-weighted 10-name basket outperforms 20 (Sharpe 1.17 vs 1.10 over
// 128 monthly vintages). Conviction tilt already concentrates ~85% of
// weight in the top 5-7 names anyway; the "long tail" of 11-20 was
// contributing diversification cost with minimal alpha lift.
export const BASKET_SIZE = 10;
export const INCEPTION_DATE = '2026-05-01';

export interface Leg {
  symbol: string;
  name: string;
  sector: string;
  entryDate: string;          // YYYY-MM-DD
  entryPrice: number;
  /** Target portfolio weight for this leg, set at entry and updated at each
   *  monthly rebalance for slots that stay in the basket. When undefined,
   *  callers should fall back to equal-weight 1/N (preserves backward
   *  compatibility with pre-conviction baskets). */
  weight?: number;
  exitDate?: string;          // YYYY-MM-DD, set when leg closes
  exitPrice?: number;
  returnPct?: number;         // (exitPrice - entryPrice) / entryPrice, locked at exit
  exitReason?: string;        // e.g. "displaced_by:TSM"
}

export interface Slot {
  slotIdx: number;
  current: Leg & { status: 'open' };
  history: Leg[];             // closed legs, append-only
}

export interface BasketAggregateRow {
  windowStart: string;
  mean: number | null;
  median: number | null;
  hitRate: number | null;
  spy: number | null;
  edgeVsSpy: number | null;
}

export interface BasketAggregates {
  asOf: string;
  today: BasketAggregateRow;
  mtd: BasketAggregateRow;
  ytd: BasketAggregateRow;
  sinceInception: BasketAggregateRow;
}

export interface AlphaBasket {
  inception: string;
  rebalanceCadence: 'monthly';
  lastRebalanceDate: string;
  nextRebalanceDate: string;
  lastUpdatedAt: string;
  modelVersion?: string;
  slots: Slot[];
  /** DEPRECATED — present only on legacy files written before 2026-05-06.
   *  New writes do NOT include this field. Read it nowhere; instead call
   *  computeAggregates() in API routes. Optional so the type still matches
   *  legacy on-disk files we might encounter mid-migration. */
  aggregates?: BasketAggregates;
}

// ---------- Date helpers (UTC-safe) ----------

function parseIso(iso: string): Date {
  return new Date(iso + 'T12:00:00Z');
}
function fmt(d: Date): string {
  return d.toISOString().slice(0, 10);
}
export function previousDay(iso: string): string {
  const d = parseIso(iso);
  d.setUTCDate(d.getUTCDate() - 1);
  return fmt(d);
}
export function priorMonthEnd(today: string): string {
  const d = parseIso(today);
  d.setUTCDate(0);
  return fmt(d);
}
export function priorYearEnd(today: string): string {
  const d = parseIso(today);
  return `${d.getUTCFullYear() - 1}-12-31`;
}
export function firstOfNextMonth(iso: string): string {
  const d = parseIso(iso);
  d.setUTCMonth(d.getUTCMonth() + 1, 1);
  return fmt(d);
}

/** Different YYYY-MM than lastRebalanceDate → rebalance. We run on weekdays
 *  via daily-refresh, so any execution is implicitly a trading day. */
export function shouldRebalance(today: string, lastRebalanceDate: string): boolean {
  return today.slice(0, 7) !== lastRebalanceDate.slice(0, 7);
}

// ---------- Live top-10 ----------

interface LiveCandidate {
  symbol: string;
  name: string;
  sector: string;
  composite: number;
  price: number;
}

export function liveTop20(scores: ScoresFile | null): LiveCandidate[] {
  const stocks = scores?.stocks ?? [];
  // Production V_blend (Steve directive 2026-05-08): use valuationScoreBlended
  // when available (0.75 peer-z + 0.25 universe-z absolute, Definition C),
  // fall back to valuationScore (peer-z) for stocks without absolute lens (banks,
  // insufficient EPS history). The eligibility gate + composite both read the
  // blended value so basket selection reflects the production V axis.
  // Production Q_blend: mirror the same pattern — prefer qualityScoreBlended
  // (0.75·peer-z + 0.25·universe-z absolute) when available, fall back to
  // qualityScore (peer-z only) for stocks without an absolute lens computed.
  const v = (s: { valuationScoreBlended?: number | null; valuationScore?: number | null }) =>
    (s.valuationScoreBlended ?? s.valuationScore) ?? null;
  const q = (s: { qualityScoreBlended?: number | null; qualityScore?: number | null }) =>
    (s.qualityScoreBlended ?? s.qualityScore) ?? null;
  return stocks
    .filter((s) => isAlphaZoneEligible(q(s), v(s)))
    .map((s) => ({
      symbol: s.symbol,
      name: s.name ?? s.symbol,
      sector: s.sector ?? 'Unknown',
      composite: compositeScore(q(s), v(s)) ?? -Infinity,
      price: s.price ?? 0,
    }))
    .filter((c) => Number.isFinite(c.composite) && c.price > 0)
    .sort((a, b) => b.composite - a.composite)
    .slice(0, BASKET_SIZE);
}

// ---------- Bootstrap ----------

/** A snapshot of the basket on the inception date — same shape as the existing
 *  live-tracker/snapshots/{date}.json files. Used to seed the basket with
 *  historically-correct entry prices when bootstrapping retroactively. */
export interface InceptionSnapshot {
  date: string;
  modelVersion?: string;
  alpha_zone: Array<{
    symbol: string;
    name?: string;
    sector?: string;
    price: number;
    rank?: number;
  }>;
}

export interface BootstrapOptions {
  /** When provided, entry prices for live-top-10 symbols come from
   *  cache.priceOnOrBefore(inception, symbol) instead of today's price.
   *  This is the "retro anchor" mode — basket = today's top-10 (matches
   *  what users see on the dashboard), entry dates = inception (so since-
   *  inception return is meaningful even though the basket was bootstrapped
   *  retroactively). Has the look-ahead-bias caveat: we picked these 20
   *  names knowing how they performed since the inception date. */
  retroPriceCache?: { histories?: Record<string, Array<{ date: string; price: number }>> };
  /** Target portfolio weights per symbol. When provided, each slot's leg gets
   *  the corresponding weight from the map. Symbols not in the map default to
   *  1 / BASKET_SIZE (equal weight). Used for conviction-weighted rules. */
  targetWeights?: Map<string, number>;
}

export function bootstrap(
  scores: ScoresFile | null,
  inception: string = INCEPTION_DATE,
  modelVersion?: string,
  snapshot?: InceptionSnapshot,
  options: BootstrapOptions = {},
): AlphaBasket {
  // Prefer the dated snapshot for seeding entry symbols + prices when
  // bootstrapping retroactively (inception in the past). Fall back to the
  // live top-10 only when no snapshot is provided.
  let entries: Array<{ symbol: string; name: string; sector: string; price: number }>;
  if (options.retroPriceCache) {
    // Retro anchor: today's live top-10 with each entry priced at the
    // inception-date close from the cache. Look-ahead biased (model picked
    // these knowing how they performed) but gives meaningful since-inception
    // returns for a basket bootstrapped after the fact.
    const top = liveTop20(scores);
    if (top.length < BASKET_SIZE) {
      throw new Error(`bootstrap: only ${top.length} alpha-eligible stocks, expected ${BASKET_SIZE}`);
    }
    const histories = options.retroPriceCache.histories ?? {};
    entries = top.map((c) => {
      const retroPrice = priceOnOrBefore(histories[c.symbol], inception);
      return {
        symbol: c.symbol,
        name: c.name,
        sector: c.sector,
        price: retroPrice ?? c.price, // fallback to today if cache missing
      };
    });
  } else if (snapshot && snapshot.alpha_zone.length >= BASKET_SIZE) {
    entries = snapshot.alpha_zone.slice(0, BASKET_SIZE).map((s) => ({
      symbol: s.symbol,
      name: s.name ?? s.symbol,
      sector: s.sector ?? 'Unknown',
      price: s.price,
    }));
  } else {
    const top = liveTop20(scores);
    if (top.length < BASKET_SIZE) {
      throw new Error(`bootstrap: only ${top.length} alpha-eligible stocks, expected ${BASKET_SIZE}`);
    }
    entries = top;
  }
  const tw = options.targetWeights;
  const slots: Slot[] = entries.map((c, i) => ({
    slotIdx: i,
    current: {
      symbol: c.symbol,
      name: c.name,
      sector: c.sector,
      entryDate: inception,
      entryPrice: c.price,
      weight: tw?.get(c.symbol) ?? (1 / BASKET_SIZE),
      status: 'open' as const,
    },
    history: [],
  }));
  return {
    inception,
    rebalanceCadence: 'monthly',
    lastRebalanceDate: inception,
    nextRebalanceDate: firstOfNextMonth(inception),
    lastUpdatedAt: new Date().toISOString(),
    modelVersion: modelVersion ?? snapshot?.modelVersion,
    slots,
  };
}

// ---------- Rebalance ----------

export interface RebalanceOptions {
  /** Target portfolio weights per symbol. When provided, every slot's current
   *  leg gets its weight refreshed (entrants get new weight; stayers get
   *  re-weighted in place — leg is NOT broken; exits close with their last
   *  weight). When undefined, weights are left as-is. */
  targetWeights?: Map<string, number>;
}

/** Rebalance the basket against today's live top-10. Closes exits with locked-in
 *  returns, opens new legs for entrants, pairs by composite rank to displaced
 *  exits (deterministic, minimal slot reshuffle). Returns the next basket state. */
export function rebalance(
  basket: AlphaBasket,
  scores: ScoresFile | null,
  today: string,
  options: RebalanceOptions = {},
): AlphaBasket {
  const live = liveTop20(scores);
  const liveSymbols = new Set(live.map((c) => c.symbol));
  const currentBySym = new Map(basket.slots.map((s) => [s.current.symbol, s]));
  const closePriceBySym = new Map<string, number>();
  for (const s of scores?.stocks ?? []) {
    if (typeof s.price === 'number' && Number.isFinite(s.price) && s.price > 0) {
      closePriceBySym.set(s.symbol, s.price);
    }
  }

  // Slots whose current symbol is no longer in the live top-10 → exits.
  const exitSlots = basket.slots.filter((s) => !liveSymbols.has(s.current.symbol));
  // Live symbols not currently in any slot → entrants.
  const entrantCandidates = live.filter((c) => !currentBySym.has(c.symbol));

  if (exitSlots.length !== entrantCandidates.length) {
    // Defensive — should be equal because both are 20-N intersect.
    throw new Error(`rebalance: ${exitSlots.length} exits vs ${entrantCandidates.length} entrants`);
  }

  // Pair exits to entrants. Both sorted by composite desc → pair index-wise.
  // For exits, "composite" means today's recompute of their current Q/V. If
  // they're not in scores.json (delisted), fall back to a very negative composite
  // so they're paired with the highest-composite entrant (no preference matters).
  const compBy = new Map<string, number>();
  for (const s of scores?.stocks ?? []) {
    // Use blended V and blended Q (production) — same logic as liveTop20 for consistency.
    const v = (s.valuationScoreBlended ?? s.valuationScore) ?? null;
    const c = compositeScore((s.qualityScoreBlended ?? s.qualityScore) ?? null, v);
    if (c != null) compBy.set(s.symbol, c);
  }
  const exitsRanked = [...exitSlots].sort(
    (a, b) => (compBy.get(b.current.symbol) ?? -1e9) - (compBy.get(a.current.symbol) ?? -1e9),
  );
  const entrantsRanked = [...entrantCandidates].sort((a, b) => b.composite - a.composite);

  const nextSlots = basket.slots.map((s) => ({ ...s, history: [...s.history] }));

  for (let i = 0; i < exitsRanked.length; i++) {
    const exitSlot = exitsRanked[i];
    const entrant = entrantsRanked[i];
    const slot = nextSlots[exitSlot.slotIdx];

    // Close the leg.
    const exitPrice = closePriceBySym.get(slot.current.symbol) ?? slot.current.entryPrice;
    const closedLeg: Leg = {
      symbol: slot.current.symbol,
      name: slot.current.name,
      sector: slot.current.sector,
      entryDate: slot.current.entryDate,
      entryPrice: slot.current.entryPrice,
      exitDate: today,
      exitPrice,
      returnPct: slot.current.entryPrice > 0
        ? (exitPrice - slot.current.entryPrice) / slot.current.entryPrice
        : 0,
      exitReason: `displaced_by:${entrant.symbol}`,
    };
    slot.history.push(closedLeg);

    // Open new leg.
    slot.current = {
      symbol: entrant.symbol,
      name: entrant.name,
      sector: entrant.sector,
      entryDate: today,
      entryPrice: entrant.price,
      weight: options.targetWeights?.get(entrant.symbol) ?? (1 / BASKET_SIZE),
      status: 'open' as const,
    };
  }

  // For stayers (slots whose symbol is still in the top-10 but didn't get
  // closed/reopened above): refresh weight from targetWeights without breaking
  // the leg. Preserves entryDate/entryPrice (and thus the leg's accumulated
  // return) but adjusts portfolio weight at rebalance time — the realistic
  // monthly behavior of a conviction-weighted PM portfolio.
  if (options.targetWeights) {
    const tw = options.targetWeights;
    for (const slot of nextSlots) {
      const w = tw.get(slot.current.symbol);
      if (w != null) slot.current.weight = w;
    }
  }

  return {
    ...basket,
    slots: nextSlots,
    lastRebalanceDate: today,
    nextRebalanceDate: firstOfNextMonth(today),
    lastUpdatedAt: new Date().toISOString(),
  };
}

// Chained-leg aggregates math lives in alpha-basket-aggregates.ts
import { computeAggregates, slotReturnInWindow } from './alpha-basket-aggregates';
export { computeAggregates, slotReturnInWindow };

// ---------- Mark-to-market (mid-month tick) ----------

/** Mid-month tick. Bumps `lastUpdatedAt`. Aggregates are NEVER written to the
 *  basket file (computed live in API routes from scores.json + daily-prices-
 *  cache.json). Membership is locked mid-month, so the on-disk shape really
 *  is stable except for `lastUpdatedAt`. The unused parameters are kept for
 *  call-site compatibility — older callers may have passed them.
 *
 *  Underscore-prefixed params: intentionally unused.
 */
export function tick(
  basket: AlphaBasket,
  _scores: ScoresFile | null,
  _cache: DailyCache | null,
  _today: string,
): AlphaBasket {
  return {
    ...basket,
    aggregates: undefined,  // strip any legacy field if present on input
    lastUpdatedAt: new Date().toISOString(),
  };
}

// ---------- Event log (for the bubble UI) ----------

export interface RebalanceEvent {
  date: string;          // exit date — same as entry date for the paired entrant
  entrant: { symbol: string; name: string; sector: string };
  exit: { symbol: string; name: string; sector: string; returnPct: number };
  slotIdx: number;
}

export interface InceptionEvent {
  date: string;
  type: 'inception';
  symbols: string[];
}

export type EventLogRow = RebalanceEvent | InceptionEvent;

export function buildEventLog(basket: AlphaBasket): EventLogRow[] {
  const events: EventLogRow[] = [];
  for (const slot of basket.slots) {
    for (let i = 0; i < slot.history.length; i++) {
      const closed = slot.history[i];
      if (closed.exitDate == null || closed.returnPct == null) continue;
      // The entrant for this exit is either the next leg in history (if there
      // was a subsequent rotation) or slot.current.
      const next = i + 1 < slot.history.length ? slot.history[i + 1] : slot.current;
      events.push({
        date: closed.exitDate,
        entrant: { symbol: next.symbol, name: next.name, sector: next.sector },
        exit: { symbol: closed.symbol, name: closed.name, sector: closed.sector, returnPct: closed.returnPct },
        slotIdx: slot.slotIdx,
      });
    }
  }
  events.push({
    date: basket.inception,
    type: 'inception',
    symbols: basket.slots
      .map((s) => {
        const earliest = s.history[0]?.entryDate === basket.inception ? s.history[0].symbol : s.current.entryDate === basket.inception ? s.current.symbol : null;
        return earliest;
      })
      .filter((s): s is string => s != null),
  });
  // Newest first
  events.sort((a, b) => b.date.localeCompare(a.date));
  return events;
}
